import { setTableBack } from "./cardBack";

/**
 * رویه‌ی میز — همان هفده میزِ اپ (TableTheme.All در یونیتی)، با همان کلیدهای سرور.
 *
 * انتخاب دیگر با بازیکن نیست: سرور سرِ هر میزِ آنلاین یکی را قرعه می‌کشد و در
 * وضعیتِ میز می‌فرستد؛ وب فقط همان را نشان می‌دهد تا وب و موبایل یک میز ببینند.
 *
 * سه گونه:
 * - `carpet`: فرشِ جدا روی صحنه‌ی میز (`.tv-carpet`) — پنج فرشِ اتاق‌های آنلاینِ قدیم.
 *   فرشِ گردِ ایوان بیضی کشیده می‌شود؛ چهار فرشِ چهارگوش (`rect`) قالیچه‌ی چهارگوش با
 *   نسبتِ خودشان‌اند، مثلِ TableScene.RectCarpet ِ اپ — در بیضی، حاشیه‌ی چهارگوششان
 *   بریده می‌شد و گوشه‌ها بی‌ریخت بیرون می‌زد.
 * - `scene`: پرده‌ی نقاشی‌شده‌ی شهرهای سفر. فرش در خودِ نقاشی است (NoCarpet در
 *   یونیتی)، پس کد نه رویه می‌کشد نه صندلی، پرده پشتِ کلِ میز می‌نشیند و
 *   حلقه‌ی نشست‌ها با `seatStretchY` کشیده می‌شود.
 * - `felt`: «سبزِ قهوه‌خانه» — نمدِ تخت، بی تصویر.
 *
 * کلیدِ ناشناخته یا خالی (میزِ کهنه، سرورِ قدیمی‌تر) = ترنجِ ایوان.
 */
export type TableKind = "carpet" | "scene" | "felt";

export type TableKey =
  | "eyvan" | "lajvard" | "mahoot" | "gelim" | "shab" | "felt"
  | "susa" | "kerman" | "yazd" | "shiraz" | "isfahan" | "kashan"
  | "hamadan" | "tehran" | "zanjan" | "tabriz" | "khorasan";

/** نام قدیمیِ همین نوع؛ بعضی جاها هنوز با این نام می‌خوانند. */
export type CarpetKey = TableKey;

export interface TableDef {
  /** هفده طرحِ ساخته‌شده (TableKey) یا کلیدِ شهری که ادمین از پنل ساخته. */
  key: TableKey | string;
  fa: string;
  /**
   * فقط شهرها: نامِ اصیلِ فرشِ همان شهر — «محتشمِ کاشان»، «قشقایی» (کاربر، ۲۲ اکتبر ۲۰۲۶: «از نامِ اصیلِ خودش
   * به جای "فرشِ فلان شهر" استفاده کن»). طرح‌ها از tools/authentic_rugs. آینه‌ی economy.cityRugFa ِ سرور.
   */
  rugFa?: string;
  kind: TableKind;
  /** فایلِ زیرِ `/art/table/` — برای felt خالی. */
  file: string;
  /** رنگِ دورِ فرش / زیرِ پرده تا تصویر برسد — لبه‌ی تیره‌ی همان میز. */
  bg: string;
  /** رنگِ خودِ نمد (فقط felt). */
  felt?: string;
  /**
   * نمدِ تختِ زیرِ تخته‌ی مارپله، تخته‌نرد و دوز — آینه‌ی TableTheme.Felt ِ اپ (نمای تخته‌ها فرش نمی‌کشد، فقط این رنگ با وینیت).
   * بی آن (شهرِ پنل، رنگِ لبه‌ای که پنل عوض کرده): `lighter(bg)`، همان TableTheme.Lighter.
   */
  boardFelt?: string;
  /** سایه‌ی دایره‌ای فقط زیرِ فرشِ گردِ ایوان معنا دارد. */
  round?: true;
  /** فرشِ چهارگوش (TableTheme.RectCarpet): قالیچه با نسبتِ خودش، نه بیضی. */
  rect?: true;
  /** بیشترین کششِ قالیچه‌ی چهارگوش تا کادرِ میز را پر کند (TableTheme.CarpetStretch). */
  stretch?: number;
  /**
   * فقط scene: کِششِ عمودیِ حلقه‌ی صندلی‌ها در پرتره (TableTheme.SeatStretchY)،
   * تا آواتارها دورِ فرشِ نقاشی‌شده بنشینند نه رویش. همراهش بیضیِ پرتره
   * (TableTheme.PortraitAspect) ۰٫۹۸ است، چون کشش فقط زیرِ ۱ اثر می‌کند.
   */
  seatStretchY?: number;
  /** ادمین از قرعه و انتخابِ میزبان درش آورده (GET /v1/tables). */
  off?: boolean;
  /** شهرِ ساخته‌شده از پنل: تصویرهایش بارگذاریِ سرورند، نه فایلِ زیرِ /art/table/. */
  custom?: boolean;
  /** نشانیِ کاملِ پرده (فقط شهرِ پنل). */
  src?: string;
  /** نشانیِ کاملِ فرشِ ایستاده‌ی دسکتاپ (فقط شهرِ پنل). */
  tallSrc?: string;
  /** نشانیِ کاملِ بندانگشتی (فقط شهرِ پنل، اختیاری). */
  thumbSrc?: string;
  /**
   * فقط شهرها: چهار گوشه‌ی فرشِ نقاشی‌شده در کادرِ ۱۱۴۳×۲۰۴۸ ِ پرده (دورِ چپ، دورِ راست، نزدیکِ راست،
   * نزدیکِ چپ؛ هشت عدد). «میزِ من» فرشِ دیگری را با پرسپکتیو دقیقاً همین‌جا می‌نشاند —
   * tools/desktop_rugs.py همین گوشه‌ها را برای فرشِ ایستاده به کار برد.
   */
  corners?: number[];
}

/**
 * نشانیِ آرتِ فرش و پرده‌ی شهرها. فرشِ اصیلِ شهرها (۲۲ اکتبرِ ۲۰۲۶، tools/authentic_rugs) همان نام‌های
 * فایلِ قبلی را دارد و ‎/art/ یک روز کش می‌شود؛ ‎?r= کشِ کهنه را دور می‌زند تا نامِ تازه زیرِ تصویرِ کهنه نیاید.
 */
export const RUG_REV = "?r=2";
export const tableArt = (file: string): string => `/art/table/${file}${RUG_REV}`;

/** کادرِ پرده‌ی همه‌ی شهرها (پرده‌ی وب ۷۲۰×۱۲۹۰ همان نسبت است). */
export const SCENE_FRAME = { w: 1143, h: 2048 } as const;

/**
 * `bg` برای صحنه‌ها همان FeltDarkِ یونیتی است: تا پرده نیامده، زمینه
 * هم‌خانواده‌ی نقاشی است و پرشِ رنگ دیده نمی‌شود.
 */
export const carpets: TableDef[] = [
  { key: "eyvan", fa: "ترنج ایوان", kind: "carpet", file: "carpet.webp", bg: "#202249", boardFelt: "#2b3074", round: true },
  { key: "lajvard", fa: "ترنج لاجورد", kind: "carpet", file: "carpet-lajvard.webp", bg: "#1e1e42", boardFelt: "#2b3074", rect: true, stretch: 1.45 },
  { key: "mahoot", fa: "ماهوت سبز", kind: "carpet", file: "carpet-mahoot.webp", bg: "#274838", boardFelt: "#1f6b57", rect: true, stretch: 1.45 },
  { key: "gelim", fa: "گلیم سرخ", kind: "carpet", file: "carpet-gelim.webp", bg: "#732e2d", boardFelt: "#8e2f33", rect: true, stretch: 1.45 },
  { key: "shab", fa: "شب نقره‌ای", kind: "carpet", file: "carpet-shab.webp", bg: "#0a0a14", boardFelt: "#1a1633", rect: true, stretch: 1.45 },
  { key: "felt", fa: "سبز قهوه‌خانه", kind: "felt", file: "", bg: "#14332a", boardFelt: "#2e6b4f", felt: "#2e6b4f" },
  { key: "susa", fa: "شوش", rugFa: "پازیریک", kind: "scene", file: "scene-susa.webp", bg: "#211a17", boardFelt: "#3a2f2a", seatStretchY: 1.25, corners: [293.7, 670, 849.1, 670, 1013.4, 1483, 130.3, 1483] },
  { key: "kerman", fa: "کرمان", rugFa: "راور کرمان", kind: "scene", file: "scene-kerman.webp", bg: "#221f31", boardFelt: "#3b3550", seatStretchY: 1.24, corners: [193, 583, 950, 583, 1049, 1490, 93, 1490] },
  { key: "yazd", fa: "یزد", rugFa: "زیلوی میبد", kind: "scene", file: "scene-yazd.webp", bg: "#1b1929", boardFelt: "#2f2b46", seatStretchY: 1.16, corners: [274.9, 779.5, 872.9, 779.5, 1061.5, 1464.5, 83, 1464.5] },
  { key: "shiraz", fa: "شیراز", rugFa: "قشقایی", kind: "scene", file: "scene-shiraz.webp", bg: "#1a1828", boardFelt: "#2e2b46", seatStretchY: 1.22, corners: [217.1, 587.5, 926.5, 587.5, 1065, 1496.5, 78.8, 1496.5] },
  { key: "isfahan", fa: "اصفهان", rugFa: "لچک‌ترنج اصفهان", kind: "scene", file: "scene-isfahan.webp", bg: "#17203f", boardFelt: "#2b3a6b", seatStretchY: 1.20, corners: [242.7, 739.5, 900.6, 739.5, 1128, 1501, 15, 1501] },
  { key: "kashan", fa: "کاشان", rugFa: "محتشم کاشان", kind: "scene", file: "scene-kashan.webp", bg: "#191828", boardFelt: "#2c2a4a", seatStretchY: 1.20, corners: [273.6, 736.5, 872.3, 736.5, 1083.6, 1505.5, 62.5, 1505.5] },
  { key: "hamadan", fa: "همدان", rugFa: "مهربان", kind: "scene", file: "scene-hamadan.webp", bg: "#1d1d25", boardFelt: "#33333f", seatStretchY: 1.20, corners: [350, 736, 796, 736, 899, 1347, 250, 1347] },
  { key: "tehran", fa: "تهران", rugFa: "میناخانی ورامین", kind: "scene", file: "scene-tehran.webp", bg: "#191927", boardFelt: "#2c2c46", seatStretchY: 1.20, corners: [189, 587, 952, 587, 1058, 1486, 86, 1486] },
  { key: "zanjan", fa: "زنجان", rugFa: "شاهسون", kind: "scene", file: "scene-zanjan.webp", bg: "#221a19", boardFelt: "#3b2d2c", seatStretchY: 1.20, corners: [323, 799, 819, 799, 1097, 1453, 50, 1453] },
  { key: "tabriz", fa: "تبریز", rugFa: "ماهی تبریز", kind: "scene", file: "scene-tabriz.webp", bg: "#181b25", boardFelt: "#2b3040", seatStretchY: 1.20, corners: [292, 719, 851, 719, 1046, 1494, 98, 1494] },
  { key: "khorasan", fa: "خراسان", rugFa: "بلوچ", kind: "scene", file: "scene-khorasan.webp", bg: "#171c28", boardFelt: "#2a3348", seatStretchY: 1.20, corners: [341.8, 792.5, 800.3, 792.5, 961.7, 1481.5, 180.4, 1481.5] },
];

export function isCarpet(k: string): boolean { return carpets.some((c) => c.key === k); }

/** تعریفِ میز برای کلیدِ سرور؛ ناشناخته = ایوان (نه اولیِ فهرست — پنل ترتیب را عوض می‌کند). */
export function tableOf(key: string | undefined): TableDef {
  return carpets.find((x) => x.key === key) ?? carpets.find((x) => x.key === "eyvan") ?? carpets[0]!;
}

/**
 * بندانگشتیِ انتخابِ میزبان: همان فرشِ ایستاده، چند کیلوبایت (tools/desktop_rugs.py،
 * thumb-<کلید>.webp). شهرِ پنل بندانگشتیِ بارگذاری‌شده‌ی خودش را دارد یا فرشِ ایستاده‌اش؛
 * نمد تصویر ندارد ("") و رنگِ خودش کشیده می‌شود.
 */
export function thumbOf(key: string): string {
  const c = tableOf(key);
  if (c.custom) return c.thumbSrc ?? c.tallSrc ?? "";
  return c.kind === "felt" ? "" : tableArt(`thumb-${c.key}.webp`);
}

/** طرح‌هایی که میزبان می‌تواند انتخاب کند — روشن‌ها، به ترتیبِ پنل. */
export function pickableTables(): TableDef[] { return carpets.filter((c) => !c.off); }

// ---------------------------------------------------------------- فهرستِ پنل

/** یک قلمِ GET /v1/tables — آینه‌ی tables.Item ِ سرور. */
export type TableItem = {
  key: string; fa?: string; bg?: string; off?: boolean;
  custom?: boolean; scene?: string; tall?: string; thumb?: string; seat_stretch_y?: number; corners?: number[];
};

/** رویداد روی window وقتی فهرستِ پنل رسید — انتخاب‌گرِ میز دوباره کشیده شود. */
export const TABLES_LOADED = "bazivan:tables";

/** پیش‌فرض‌های هفده طرحِ ساخته‌شده، تا فهرستِ تازه‌ی پنل نام یا رنگی را که پاک کرده برگرداند. */
const builtins: TableDef[] = carpets.map((c) => ({ ...c }));
const BG = /^#[0-9a-fA-F]{6}$/;
const ASSET = /^\/v1\/assets\/[^/]+$/;

/**
 * فهرستِ پنل را روی طرح‌ها می‌نشاند: نام و رنگ و خاموشیِ طرحِ ساخته‌شده، و شهرهای تازه با
 * پرده و فرشِ ایستاده‌ی خودشان. ترتیبِ `carpets` همان ترتیبِ پنل می‌شود (فهرستِ انتخابِ میزبان).
 * قلمِ خراب کنار می‌رود و بقیه می‌مانند؛ طرحِ ساخته‌شده‌ای که نیامده با پیش‌فرض ته فهرست است.
 * `origin` ریشه‌ی سرور است، چون نشانیِ بارگذاری‌ها نسبی است (/v1/assets/…).
 */
export function applyTables(items: TableItem[], origin = ""): void {
  const next: TableDef[] = [];
  const seen = new Set<string>();
  for (const it of items) {
    if (!it || typeof it.key !== "string" || seen.has(it.key)) continue;
    const base = builtins.find((b) => b.key === it.key);
    const bg = typeof it.bg === "string" && BG.test(it.bg) ? it.bg : undefined;
    const fa = typeof it.fa === "string" && it.fa.trim() ? it.fa.trim() : undefined;
    if (base) {
      // رنگِ لبه‌ی پنل نمدِ تخته را هم عوض می‌کند — مثلِ TableTheme.Override ِ اپ (Felt = Lighter(bg))
      next.push({ ...base, fa: fa ?? base.fa, bg: bg ?? base.bg, boardFelt: bg ? undefined : base.boardFelt, off: it.off === true });
    } else if (it.custom && fa && bg && ASSET.test(it.scene ?? "") && ASSET.test(it.tall ?? "")) {
      next.push({
        key: it.key, fa, kind: "scene", file: "", bg, custom: true, off: it.off === true,
        src: origin + it.scene, tallSrc: origin + it.tall, thumbSrc: ASSET.test(it.thumb ?? "") ? origin + it.thumb : undefined,
        seatStretchY: typeof it.seat_stretch_y === "number" && it.seat_stretch_y >= 1 && it.seat_stretch_y <= 1.4 ? it.seat_stretch_y : 1.2,
        corners: Array.isArray(it.corners) && it.corners.length === 8 && it.corners.every((n) => typeof n === "number" && Number.isFinite(n)) ? it.corners : undefined,
      });
    } else continue;
    seen.add(it.key);
  }
  for (const b of builtins) if (!seen.has(b.key)) next.push({ ...b });
  carpets.splice(0, carpets.length, ...next);
  // میزی که پیش از رسیدنِ فهرست کلیدش آمده بود (شهرِ تازه → ایوان) حالا درست کشیده شود
  if (typeof document !== "undefined") showCarpet(wanted);
  if (typeof window !== "undefined") window.dispatchEvent(new Event(TABLES_LOADED));
}

/** نمدِ روشنِ هم‌خانواده‌ی یک رنگِ لبه — آینه‌ی TableTheme.Lighter ِ اپ (هر کانال ×۱٫۵۵ + ۰٫۰۱). */
export function lighter(hex: string): string {
  const ch = (i: number) => Math.min(255, Math.round(parseInt(hex.slice(i, i + 2), 16) * 1.55 + 2.55));
  return "#" + [1, 3, 5].map((i) => ch(i).toString(16).padStart(2, "0")).join("");
}

/** پس‌زمینه‌ی فرشِ ایستاده: `tall-<کلید>.webp`، و برای نمد خودِ رنگش. */
export function tallArt(c: TableDef): string {
  if (c.kind === "felt") return `linear-gradient(${c.felt}, ${c.felt})`;
  return `url("${c.tallSrc ?? tableArt(`tall-${c.key}.webp`)}")`;
}

/**
 * میزی که سرِ آنیم — از **وضعیتِ سرور**.
 *
 * رنگ و تصویر با متغیرهای CSS و `data-table` روی <html> می‌رود. صحنه‌ی میز
 * (`scene/table.ts`) با رویدادِ [[TABLE_CHANGED]] چیدمانش را از نو می‌کشد: میزِ
 * scene صندلی و فرشِ بیضی ندارد و نشست‌هایش کشیده‌ترند.
 */
/** بیضیِ پرتره‌ی میزهای scene — TableTheme.PortraitAspect. */
export const SCENE_PORTRAIT_ASPECT = 0.98;

/** رویداد روی window وقتی میزِ نشان‌داده عوض شد — صحنه‌ی میز چیدمانش را از نو می‌کشد. */
export const TABLE_CHANGED = "bazivan:table";

/**
 * میزِ نشان‌داده: شهر (پرده) و فرش. «میزِ من» (کاربر، ۲۱ اکتبر ۲۰۲۶) هر شهری را با هر فرشی
 * می‌چیند و کلیدش همان رشته‌ی فرشِ پروتکل است — آینه‌ی match.SplitTable ِ سرور:
 *
 *   "lajvard" فرشِ تنها · "isfahan" شهر با فرشِ خودش · "isfahan+lajvard" · "+isfahan" (بی شهر، فرشِ اصفهان)
 */
export type TableView = { key: string; city?: TableDef; rug: TableDef; back?: string };

const isClassic = (k: string) => builtins.some((b) => b.key === k && b.kind !== "scene");

/** کلید → شهر و فرش؛ نیمه‌ی ناشناخته = ایوان (بی شهر). */
export function viewOf(key: string | undefined): TableView {
  // پشتِ کارتِ میز («میزهای من»): پسوندِ «@» — آینه‌ی match.TableBack
  const [k, back] = (key ?? "").split("@", 2) as [string, string | undefined];
  const [c, r] = k.includes("+") ? k.split("+", 2) as [string, string] : isClassic(k) ? ["", k] : [k, k];
  const city = c && isCarpet(c) && tableOf(c).kind === "scene" ? tableOf(c) : undefined;
  const rug = isCarpet(r || c) ? tableOf(r || c) : tableOf("eyvan");
  return { key: k, city, rug: city && !isCarpet(r || c) ? city : rug, back: back || undefined };
}

/** شهر و فرش → کلید، کوتاه‌ترین شکل — آینه‌ی match.CanonTable ِ سرور. */
export function tableKey(city: string, rug: string): string {
  const r = rug || city || "eyvan";
  if (!city) return isClassic(r) ? r : `+${r}`;
  return r === city ? city : `${city}+${r}`;
}

/** نامِ فرش: نامِ اصیلِ فرشِ شهر («قشقایی»)، شهرِ پنل «فرشِ <شهر>»، و بقیه نامِ خودشان («ترنجِ لاجورد»). */
export function rugName(r: TableDef): string {
  if (r.kind !== "scene") return r.fa;
  return r.rugFa ?? `فرش ${r.fa}`;
}

/** نامِ میز از محتوایش: «اصفهان» · «اصفهان با فرشِ قشقایی» · «ترنجِ لاجورد» · «محتشمِ کاشان». */
export function tableTitle(key: string): string {
  const { city, rug } = viewOf(key);
  if (!city) return rugName(rug);
  return rug === city ? city.fa : `${city.fa} با فرش ${rugName(rug)}`;
}

/** فرشِ رایگان — آینه‌ی economy.FreeRugs. */
export const FREE_RUGS = ["eyvan", "mahoot", "felt"];

let current: TableView = viewOf("eyvan");
/** کلیدی که آخرین بار خواسته شد — شاید شهری باشد که فهرستِ پنل هنوز نرسیده. */
let wanted: string | undefined;

/** میزی که همین حالا نشان داده می‌شود. */
export function currentView(): TableView { return current; }

/** فرشِ تنهای این طرح، به شکلِ پس‌زمینه‌ی تخت: فرشِ چهارگوش/گرد خودش، فرشِ شهر همان ایستاده. */
function flatArt(r: TableDef): string {
  if (r.kind === "felt") return `linear-gradient(${r.felt}, ${r.felt})`;
  if (r.kind === "scene") return tallArt(r);
  return `url("/art/table/${r.file}")`;
}

/**
 * رویه‌ی میزِ جاری: کلیدِ اولین وضعیتی که هست — هر میزی که باز است، و نبود، اتاق.
 *
 * فهرست را App یک‌جا می‌دهد تا هیچ بازی‌ای جا نماند. شلم جا مانده بود (کاربر، ۲۸ اکتبر ۲۰۲۶: «با یک
 * طرحِ میز شروع می‌کنیم و بعد از پیدا شدنِ حاکم به فرشِ دیگری عوض می‌شود»): سرِ آس‌کشی میز رویه‌ی
 * پیش‌فرض یا رویه‌ی اتاق را داشت و فرشِ خودِ میز تازه با پخش می‌نشست.
 */
export function carpetOfTable(...states: ({ carpet?: string } | null | undefined)[]): string | undefined {
  for (const s of states) if (s?.carpet !== undefined && s.carpet !== null) return s.carpet;
  return undefined;
}

export function showCarpet(key: string | undefined): void {
  wanted = key;
  const v = viewOf(key);
  const changed = v.city !== current.city || v.rug !== current.rug;
  current = v;
  const { city, rug } = v;
  const root = document.documentElement;
  const css = root.style;
  root.dataset.table = city ? "scene" : rug.kind === "felt" ? "felt" : "carpet";
  css.setProperty("--carpet-bg", (city ?? rug).bg);
  // نمدِ تختِ مارپله، تخته‌نرد و دوز (style.css، .bt-marpele/.bt-nard/.bt-dooz) — همان رنگی که اپ زیرِ تخته می‌کشد. فرشِ شهر
  // بی شهر («+isfahan») در اپ چیدمانِ لاجورد را دارد (TableTheme.Compose)، پس نمدش هم همان است.
  const felt = city ?? (rug.kind === "scene" ? tableOf("lajvard") : rug);
  css.setProperty("--board-felt", felt.boardFelt ?? lighter(felt.bg));
  css.setProperty("--table-scene", city ? `url("${city.src ?? tableArt(city.file)}")` : "none");
  // فرشِ تخت (گوشی، بی شهر): فرشِ خودش؛ نمد رنگِ نمد روی زمینه‌ی تیره‌ی هم‌خانواده
  css.setProperty("--carpet-url", city ? "none" : flatArt(rug));
  // فرشِ ایستاده‌ی دسکتاپ (geometry.ts، isTall): هر میز جز نمد تصویرِ ایستاده‌ی خودش را دارد
  // (tools/desktop_rugs.py) — شهرها فرشِ همان پرده، تخت‌شده
  css.setProperty("--carpet-tall", tallArt(rug));
  // شهرِ پشتِ میزِ ایستاده‌ی دسکتاپ: بالای پرده تا لبه‌ی دورِ فرشِ نقاشی‌شده (style.css، desk). ساخته‌شده‌ها
  // برشِ تیزِ desk-<شهر>.webp دارند (tools/desktop_rugs.py)؛ شهرِ پنل خودِ پرده‌ی ۱۱۴۳×۲۰۴۸ است که همان‌جا
  // بریده دیده می‌شود. نسبت از گوشه‌ها: پهنای کادر / لبه‌ی دورِ فرش.
  const far = city?.corners?.length === 8 ? Math.round(Math.min(city.corners[1]!, city.corners[3]!)) : 740;
  css.setProperty("--table-desk", city ? `url("${city.custom ? city.src : `/art/table/desk-${city.key}.webp`}")` : "none");
  css.setProperty("--desk-ar", `${SCENE_FRAME.w} / ${far}`);
  // «میزِ من» ِ گوشی: فرشِ دیگر با پرسپکتیو روی فرشِ نقاشی‌شده‌ی شهر (scene/table.ts، tv-rugproj)
  css.setProperty("--rug-proj", city && rug !== city ? tallArt(rug) : "none");
  css.setProperty("--carpet-shadow", rug.round && !city
    ? "radial-gradient(circle closest-side at 50% 50%, #00000066 0 100%, #00000038 107%, #00000000 116%)"
    : "none");
  // پشتِ کارتِ میزِ «میزهای من» برای همه‌ی سرِ همین میز؛ بیرون از میز پشتِ خودم
  setTableBack(v.back ?? "", root);
  if (changed && typeof window !== "undefined") window.dispatchEvent(new Event(TABLE_CHANGED));
}

if (typeof document !== "undefined") showCarpet("eyvan");
